import dayjs from 'dayjs';
import { ElementNodeBuilder } from 'Editor/services/Model';
import { ELEMENTS } from 'Editor/services/consts';
import { BaseViewBuilder } from '../BaseViewBuilder';
import { FieldElement } from './FieldElement/FieldElement';

export class FieldViewBuilder
  extends BaseViewBuilder
  implements Editor.Visualizer.IViewbuilder<Editor.Elements.FieldElement>
{
  ATTRIBUTE_MAPPER: Editor.Visualizer.ATTRIBUTE_MAPPER_TYPE<Editor.Elements.FieldElement> = {
    // id
    id: this.GENERIC_ATTRIBUTE_MAPPER.id,
    // parent id
    parent_id: this.GENERIC_ATTRIBUTE_MAPPER.parent_id,
    // element_type
    element_type: {
      parse: this.GENERIC_ATTRIBUTE_MAPPER.element_type.parse,
      render: (json, node: Editor.Elements.FieldElement) => {
        node.setAttribute('element_type', ELEMENTS.FieldElement.ELEMENT_TYPE);
      },
      remove: this.GENERIC_ATTRIBUTE_MAPPER.element_type.remove,
    },
    // type
    t: {
      parse: (node: Editor.Elements.FieldElement, builder: ElementNodeBuilder) => {
        if (node.dataset.type != null) {
          builder.addProperty('t', node.dataset.type);
        }
      },
      render: (json, node: Editor.Elements.FieldElement) => {
        if (json.properties && json.properties.t != null) {
          node.dataset.type = json.properties.t;
        }
      },
      remove: (node: Editor.Elements.FieldElement) => {
        delete node.dataset.type;
      },
    },
    // reference
    r: {
      parse: (node: Editor.Elements.FieldElement, builder: ElementNodeBuilder) => {
        if (node.dataset.ref != null) {
          builder.addProperty('r', node.dataset.ref);
        }
      },
      render: (json, node: Editor.Elements.FieldElement) => {
        if (json.properties && json.properties.r != null) {
          node.dataset.ref = json.properties.r;
        }
      },
      remove: (node: Editor.Elements.FieldElement) => {
        delete node.dataset.ref;
      },
    },
    // format
    f: {
      parse: (node: Editor.Elements.FieldElement, builder: ElementNodeBuilder) => {
        if (node.dataset.format != null) {
          builder.addProperty('f', node.dataset.format);
        }
      },
      render: (json, node: Editor.Elements.FieldElement) => {
        if (json.properties && json.properties.f != null) {
          node.dataset.format = json.properties.f;
        }
      },
      remove: (node: Editor.Elements.FieldElement) => {
        delete node.dataset.format;
      },
    },
    // caption label
    cpt: {
      parse: (node: Editor.Elements.FieldElement, builder: ElementNodeBuilder) => {
        if (node.dataset.cpt) {
          builder.addProperty('cpt', node.dataset.cpt);
        }
      },
      render: (json, node: Editor.Elements.FieldElement) => {
        if (json.properties && json.properties.cpt) {
          node.dataset.cpt = json.properties.cpt;
        }
      },
      remove: (node: Editor.Elements.FieldElement) => {
        delete node.dataset.cpt;
      },
    },
    // link
    l: {
      parse: (node: Editor.Elements.FieldElement, builder: ElementNodeBuilder) => {
        if (node.dataset.link != null) {
          builder.addProperty('l', node.dataset.link);
        }
      },
      render: (json, node: Editor.Elements.FieldElement) => {
        if (json.properties && json.properties.l != null) {
          node.dataset.link = `${json.properties.l}`;
        }
      },
      remove: (node: Editor.Elements.FieldElement) => {
        delete node.dataset.link;
      },
    },
    error: {
      parse: () => {},
      render: (json, node: Editor.Elements.FieldElement) => {
        if (json.properties && json.properties.error) {
          node.dataset.error = 'true';
        }
      },
      remove: (node: Editor.Elements.FieldElement) => {
        delete node.dataset.error;
      },
    },

    // clipboard only
    tempCrossReferenceTarget: {
      parse: (node: HTMLElement, builder: ElementNodeBuilder) => {
        if (node.dataset.tempCrossReferenceTarget) {
          builder.addClipboardProperty(
            'tempCrossReferenceTarget',
            node.dataset.tempCrossReferenceTarget,
          );
        }
      },
      render: (json: Editor.Data.Node.Data, node: HTMLElement) => {},
      remove: (node: HTMLElement) => {
        delete node.dataset.tempCrossReferenceTarget;
      },
    },
  };

  get attributeMapper() {
    return this.ATTRIBUTE_MAPPER;
  }

  build(json: Editor.Data.Node.FieldData) {
    const node = new FieldElement(this.Visualizer, this.Data);

    Object.keys(this.ATTRIBUTE_MAPPER).forEach((prop) => {
      this.ATTRIBUTE_MAPPER[prop].render(json, node);
    });

    if (node.dataset.type === 'd') {
      node.setContent(dayjs(this.Data.fields?.get('date')).format(node.dataset.format));
    }
    if (node.dataset.type === 'mt') {
      node.setContent(this.Data.fields?.get('title') as string);
    }

    return node;
  }

  shouldRenderChildren(json: Editor.Data.Node.FieldData) {
    if (json.properties && json.properties.t) {
      //@ts-expect-error type matching
      if (!ELEMENTS.FieldElement.TYPES.EDITABLE.includes(json.properties.t)) {
        return false;
      }
    }
    return true;
  }
}
